import { loadRazorpay } from "./loadRazorpay";

/**
 * Shape of the Razorpay-related fields returned by subscription upsert /
 * switch-billing-mode responses.
 */
export interface IRazorpayResponseData {
  billing_mode?: "RECURRING" | "PREPAID";
  rzp_type?: "none" | "subscription" | "checkout" | string;
  rzp_key?: string;
  gateway_subscription_id?: string;
  sub_id?: string;
  rzp_order?: { id?: string; amount?: number; currency?: string } | null;
}

export interface ILaunchRazorpayOptions {
  /** Response payload carrying the rzp_* fields. */
  data: IRazorpayResponseData;
  /** Checkout display name (usually the plan name). */
  name: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  /** Called after the user completes payment. */
  onSuccess?: (response: any) => void;
  /** Called on any error (SDK load / missing order / payment failure). */
  onError?: (message: string) => void;
}

/**
 * Opens the Razorpay checkout for a subscription (RECURRING) or a one-time
 * order (PREPAID), based on the response's rzp_type / billing_mode.
 *
 * Returns true if checkout was opened, false if it could not be launched.
 */
export const launchRazorpay = async ({
  data,
  name,
  description,
  prefill,
  onSuccess,
  onError,
}: ILaunchRazorpayOptions): Promise<boolean> => {
  const {
    billing_mode: respBillingMode,
    rzp_type,
    rzp_key,
    gateway_subscription_id,
    sub_id,
    rzp_order,
  } = data;

  /* RECURRING → Razorpay subscription (rzp_type "subscription").
     PREPAID   → Razorpay one-time order/checkout (rzp_type "checkout"). */
  const isSubscription =
    rzp_type === "subscription" || respBillingMode === "RECURRING";

  const baseOptions: any = {
    key: rzp_key || "",
    name,
    description,
    image: "/logo.png",
    handler: function (response: any) {
      onSuccess?.(response);
    },
    prefill: prefill ?? { name: "", email: "", contact: "" },
    theme: { color: "#0074d4" },
  };

  let options: any;
  if (isSubscription) {
    options = {
      ...baseOptions,
      subscription_id: gateway_subscription_id || sub_id,
      recurring: true,
    };
  } else {
    if (!rzp_order?.id) {
      onError?.("Payment order could not be created. Please try again.");
      return false;
    }
    options = {
      ...baseOptions,
      order_id: rzp_order.id,
      amount: rzp_order.amount,
      currency: rzp_order.currency,
      recurring: false,
    };
  }

  const loaded = await loadRazorpay();
  if (!loaded) {
    onError?.("Razorpay SDK failed to load");
    return false;
  }

  // @ts-ignore - Razorpay is attached to window by the loaded SDK.
  const rzp = new window.Razorpay(options);
  rzp.on("payment.failed", function (failResponse: any) {
    onError?.(
      failResponse?.error?.description || "Payment failed. Please try again.",
    );
  });
  rzp.open();
  return true;
};
